import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from './SideBar';
import Header from './Header';
import AutoWriteEditor from './AutoWriteEditor';
import Qalert from '../shared/Qalert';
import ScreenSizeWarning from '../shared/ScreenSizeWarning';
import { IconButton } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { APP_STORE } from '../../utility/store/redux';
import { useDispatch, useSelector } from 'react-redux';
import publicApi from '../../api/publicApi';
import { useRouter } from 'next/router';
import { actionTypes } from '../../constants/actionTypes';
import {
  useSession,
  useQalert,
  usePreFetchData,
  useIntercomUserInfo,
  useIsContentAIUser,
  useSchema,
  useIsFreeTrialContentAIUser,
  useSessionKeepAlive,
} from '../../utility/services/customHooks';
import SessionWarningModal from './SessionWarningModal';
import ThreeBarLoader from '../shared/ThreeBarLoader';
import { useIsSubscriberUser, useIsInternalUser } from '../../utility/services/customHooks';
import { isProductionEnv, USER_CLASSIFICATION } from '../../utility/common';
import { aiSeoExclusiveNavKeys } from '../../constants/navigationMeta';
import QsectionHeader from './QsectionHeader';
import { useView } from '../../utility/services/uiHelpers';
import { AuthenticationStatus } from '../../auth/types';
import ManageSubscriptions from '../shared/quattr-actions/components/ManageSubscriptions';
import UnderMaintenance from '../content-recommendations/common/underMaintenance';
import { isServerSide } from '../../utility/rendering';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import CustomAlert from '../shared/CustomAlert';
import { SUBSCRIPTION_STATUS } from '../../constants/common';
import DesktopAlert from './DesktopAlert';

const timeout = 1000 * 60 * process.env.NEXT_PUBLIC_SESSION_EXPIRY;
if (!timeout) {
  throw new Error(`No timeout set. This will cause errors!`);
}

const EXPECTED_SCREEN_WIDTH = 1280;
const LAST_APP_PATH_KEY = 'qwa_last_app_path';

/**
 * @type {import("next").NextComponentType}
 */
const PrivateLayout = (props) => {
  const isMobile = useView('mobileOnly');
  const [open, setOpen] = React.useState(false);
  const router = useRouter();
  const { asPath } = router;
  const pathname = asPath;
  const schemaName = useSchema();
  const customerInfo = useSelector((state) => state.root.customerInfo || []);

  const session = useSession();
  const {
    showWarning,
    secondsRemaining,
    isAbsoluteExpiry,
    extendSession,
    logout: sessionLogout,
    reLogin,
    cancelWarning,
  } = useSessionKeepAlive();
  const [options, setOptions] = React.useState([
    { text: 'AI SEO Suite', value: 'AI_SEO_SUITE', disabled: true, hidden: true },
    { text: 'Content AI', value: 'CONTENT_AI', disabled: true, hidden: true },
    { text: 'AI Tools', value: 'QWA_TOOLS', disabled: true, hidden: true },
  ]);

  const getUserType = () => {
    const sessionData = session.session;
    if (!sessionData || isServerSide()) {
      return undefined;
    }

    const userInfo = sessionData?.user;

    if (!userInfo) {
      return undefined;
    }
    if (userInfo?.userType == undefined) {
      const customerData = customerInfo.find((o) => o.cust_code == schemaName);
      const isContentAi = customerData?.sidebar_link_config?.['is-content-enabled-only'];
      if (isContentAi) {
        return USER_CLASSIFICATION.CONTENT_AI;
      }
    }

    return userInfo?.userType;
  };

  const [selectedUserType, setSelectedUserType] = React.useState(null);
  const dispatch = useDispatch();
  const isContentAIUser = useIsContentAIUser();
  // const paths = ['/app/trends/top-trends', '/app/trends/competitive-trends/[id]', '/app/trends/growth-trends/[id]', '/app/opportunities/top-opportunities', '/app/usability-recommendations'];
  const paths = [
    '/app/content-recommendations/editor/',
    '/app/content-recommendations/details/',
    '/app/content-recommendations/content-brief/',
    '/app/content-recommendations/content-brief/details/',
    '/app/content-recommendations/add-page-optimize',
    '/app/manage-queries/intent-settings',
    '/app/manage-categories',
    '/app/manage-queries/query-classification-rules',
    '/app/manage-lists',
    '/app/discoverability/internal-linking-recommendations/view-sample/',
    '/app/discoverability/autonomous-internal-linking/',
    '/app/content-ai-home',
    '/app/ai-tools-home',
    '/app/ai-seo-suite-home',
    '/app/manage-account',
    '/app/add-credits',
    '/app/settings/setup/add-competitors',
    '/app/settings/setup/add-domains',
    '/app/settings/setup/add-branded-keywords',
    '/app/settings/setup/override-system-prompts',
    '/app/content-index',
    '/app/settings/setup/brand-style',
    '/app/settings/setup/cms-templates',
  ];
  const hideQheaderforPaths = [
    '/app/trends/growth-trends',
    '/app/trends/competitive-trends',
    '/app/trends/ai-visibility',
    '/app/content-recommendations/details/',
    '/app/ranking-factors/ranking-factors-summary',
    '/app/manage-queries/intent-settings',
    '/app/manage-categories',
    '/app/manage-queries/query-classification-rules',
    '/app/manage-lists',
    '/app/settings/setup/add-competitors',
    '/app/settings/setup/add-domains',
    '/app/settings/setup/add-branded-keywords',
    '/app/settings/setup/override-system-prompts',
    '/app/settings/setup/brand-style',
    '/app/settings/setup/cms-templates',
    // Team Members renders its own page header card, so suppress the auto one.
    '/app/settings/team-members',
    // Same for Admin > Credits & Limits. It needs a header whose title changes
    // ("Credits & Limits" / "Change History") as the view toggles, which the
    // nav-driven QsectionHeader cannot do, so it draws its own and this
    // suppresses the duplicate. Admin > Tenants is untouched and keeps the
    // auto header.
    '/app/settings/admin/credits-and-limits',
    // '/app/discoverability/internal-linking-recommendations',
    '/app/content-ideas',
  ];
  const isFullScreenRoute = (paths, pathName) => paths.some((path) => pathName.includes(path));
  const shoudHideQheader = hideQheaderforPaths.some((path) => router.pathname.includes(path));

  const showSubscriberBanner = useSelector((state) => state.root.showSubscriberBanner);
  const subscriberMeta = useSelector((state) => state.root.subscriberMeta);

  const usabilityList = useSelector((state) => state.root.usabilityRecommendationList);
  const usabilityExportFilters = useSelector((state) => state.root.usabilityExportFilters);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');
  const showFullScreen =
    useSelector((state) => state.root.showFullScreen) ||
    isFullScreenRoute(paths, router.pathname) ||
    router.pathname.startsWith('/app/ai-tools/');
  const sidebarOpen = useSelector((state) => state.root.sidebarOpen);
  const customPopupConfig = useSelector((state) => state.root.customPopupConfig);
  const [snackConfig, toogleSnackBar] = useQalert();
  const isSubscriberUser = useIsSubscriberUser();
  const isInternalUser = useIsInternalUser();
  // const { authState, authService } = useOktaAuth();

  const loadersArray = useSelector((state) => state.root.loadersArray || []);
  const [
    fetchTopTrends,
    fetchUsabilityRecommendation,
    fetchCustomerInfo,
    getUserPermissions,
    getLooksData,
    fetchUsers,
  ] = usePreFetchData();

  const isSiteUsabilityPage = router.pathname.includes('/app/usability-recommendations');

  const exportHeaders = [
    { label: 'Site', key: `site` },
    { label: 'Rank', key: `rank` },
    { label: 'Usability Improvement', key: `usability_improvement` },
    { label: 'Category', key: `lh_category` },
    { label: 'Resource URL', key: `resourceurl` },
    { label: 'Top Pages', key: `top_pages` },
    { label: 'Top Cat Subcat', key: `top_cat_subcat` },
    { label: 'Monthly Clicks', key: `monthly_clicks` },
    { label: 'Final URL List', key: `finalurl_list` },
    { label: 'Usability Impact', key: `usability_impact` },
    { label: 'URLs Impacted', key: `urls_impacted` },
    { label: 'UUID', key: `uuid` },
    { label: 'Resolution', key: `resolution` },
  ];
  // commented beacuse no need this as we are using logrocket
  // useEffect(() => {
  //   if (session?.session?.user?.email) {
  //     amplitude?.init(process.env.NEXT_PUBLIC_AMPLITUDE_KEY, session?.session?.user?.email, {
  //       defaultTracking: {
  //         sessions: true,
  //       },
  //     });

  //     const sessionReplayTracking = sessionReplayPlugin({
  //       sampleRate: 1, // change this afterwords according to the quota given by amplitude.
  //     });

  //     // Enable session replay
  //     amplitude?.add(sessionReplayTracking);
  //   }
  // }, [session]);

  const isFreeTrailContentAIUser = useIsFreeTrialContentAIUser();

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const currentPath = `${window.location.pathname}${window.location.search}`;
    if (!currentPath.startsWith('/app/')) return;

    sessionStorage.setItem(LAST_APP_PATH_KEY, currentPath);
  }, [router.asPath]);

  // React.useEffect(() => {
  //   setOpen(sidebarOpen);
  // }, [sidebarOpen]);

  React.useEffect(() => {
    const handleStorageEvent = (event) => {
      if (event.key === 'logged_out') {
        window.location.replace('/');
        return;
      }
      if (event.key === 'switched_account') {
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorageEvent);
    return () => window.removeEventListener('storage', handleStorageEvent);
  }, []);

  const [currentWindowWidth, setCurrentWindoWidth] = useState(1500);
  useEffect(() => {
    // const updateWidth = () => setCurrentWindoWidth(document?.documentElement?.clientWidth || 0);
    const updateWidth = () => setCurrentWindoWidth(window?.innerWidth || 0);
    window?.addEventListener('resize', updateWidth);
    updateWidth();
    return () => window?.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const handleResize = () => {
        // Remove auto-close behavior for desktop as we want overlay behavior
        // const isSmallScreen = document.documentElement.clientWidth <= 1800;
        // console.log('isSmallScreen', isSmallScreen, open);
        // if (isSmallScreen) {
        //   setOpen(false);
        // }
      };
      // Run once on mount
      handleResize();

      // Optional: still listen for future resizes
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [
    router,
    //// dont add `open` dependency
  ]);

  React.useEffect(() => {
    // for easy debugging on non prod environments
    if (!isProductionEnv) {
      window.APP_STORE = APP_STORE;
    }
    const isMobile = document.documentElement.clientWidth < 768;
    if (isMobile) {
      setOpen(false);
    }
    if (session.session && session.authState !== AuthenticationStatus.Loading) {
      if (!isSubscriberUser) {
        // const profile = getUserDetails();
        const profile = session.session;

        if (profile && !isInternalUser) {
          handleSurvey(profile);
        }

        // fetchTopTrends(); // sf cost reduction measure
        // fetchUsabilityRecommendation();
        // this call is a dependency, hence the await
        getUserPermissions();
        getLooksData();
        fetchUsers();
      }
      // this call has a dependency in getUserPermissions();
      fetchCustomerInfo(session.session);
    }
  }, [session?.session]);

  const handleSurvey = async (profile) => {
    try {
      const result = await publicApi.post('public/surveyValidation', { email: profile.email });

      if (result.isValidUserForSurvey && window.delighted) {
        //  only to be called for selected users
        const surveyPayload = {
          email: profile.email,
          name: profile.name,
          properties: {
            company: profile.selected_schema,
            ...result.isValidUserForSurvey,
          },
        };
        window.delighted.survey(surveyPayload);
      }
    } catch (error) {
      console.log('handleSurvey: error', error);
    }
  };
  useIntercomUserInfo();
  const toggleDrawerOnClicks = useCallback(
    (open) => {
      const allChartsEle = document.querySelectorAll('[chartId]');
      allChartsEle.forEach((chartDiv) => {
        if (chartDiv) {
          chartDiv.dispatchEvent(new Event('resize'));
        }
      });
      dispatch({
        type: actionTypes.TOGGLE_SIDEBAR,
        sidebarOpen: open,
      });
      setOpen(open);
    },
    [dispatch]
  );

  const hoverTimerRef = useRef(null);
  const triggerChartsResize = () => {
    const allChartsEle = document.querySelectorAll('[chartId]');
    allChartsEle.forEach((chartDiv) => {
      chartDiv?.dispatchEvent(new Event('resize'));
    });
  };

  const toggleDrawer = useCallback(
    (open) => {
      // ---- Closing (Instant) ----
      if (!open) {
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }

        dispatch({
          type: actionTypes.TOGGLE_SIDEBAR,
          sidebarOpen: false,
        });
        setOpen(false);

        // ✅ Re-layout charts on close also
        triggerChartsResize();
        return;
      }

      // ---- Opening (Delayed) ----
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }

      hoverTimerRef.current = setTimeout(() => {
        dispatch({
          type: actionTypes.TOGGLE_SIDEBAR,
          sidebarOpen: true,
        });
        setOpen(true);

        // ✅ Re-layout charts on open
        triggerChartsResize();

        hoverTimerRef.current = null;
      }, 400);
    },
    [dispatch]
  );

  const getHeader = () => {
    if (usabilityList && usabilityList.length) {
      if (usabilityList[0].lh_category == 'MOBILE') {
        return [
          // { label: 'Site', key: `site` },
          { label: 'Rank', key: `rank` },
          { label: 'Id', key: `row_id` },
          { label: 'Usability Improvement', key: `usability_improvement` },
          { label: 'Category', key: `lh_category` },
          { label: 'Page URL', key: `finalurl_list` },
          // { label: 'Top Pages', key: `top_pages` },
          // { label: 'Top Cat Subcat', key: `top_cat_subcat` },
          { label: 'Organic Clicks Impacted', key: `monthly_clicks` },
          // { label: 'Final URL List', key: `finalurl_list` },
          // { label: 'Usability Impact', key: `usability_impact` },
          // { label: 'URLs Impacted', key: `urls_impacted` },
          { label: 'UUID', key: `uuid` },
          { label: 'Recommendation Status', key: `resolution` },
        ];
      }
    }
    return exportHeaders;
  };

  const handleExportAll = async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportProgress('Exporting...');

    try {
      const filters = usabilityExportFilters || {};

      // Use fetch with blob response type to download CSV from Looker
      const response = await fetch('/api/usability_recommendation_paginated', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          auth_token: document.cookie
            .split('; ')
            .find((c) => c.startsWith('user_auth_token='))
            ?.split('=')[1],
        },
        body: JSON.stringify({ filters }),
      });

      if (!response.ok) {
        toogleSnackBar({ type: 'error', text: 'Failed to export data. Please try again.', open: true });
        return;
      }

      // Download the CSV blob
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Quattr Technical Recommendations.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      toogleSnackBar({ type: 'error', text: 'Failed to export data. Please try again.', open: true });
    } finally {
      setIsExporting(false);
      setExportProgress('');
    }
  };

  const SubscriberBanner =
    showSubscriberBanner && subscriberMeta && !isMobile ? (
      <div style={{ background: '#a8dadc', textAlign: 'center', height: isMobile ? 'auto' : '30px', color: 'black' }}>
        <p style={{ lineHeight: isMobile ? 'inherit' : '30px', margin: isMobile ? '0px 39px' : 0 }}>
          <b>
            {subscriberMeta.isInTrial
              ? `Your free trial period ends in ${subscriberMeta.trialDaysRemaining} days.`
              : `Your free trial is about to expire. Ensure uninterrupted service by taking action now.`}
          </b>
          {` `}
          {/* {!subscriberMeta.hasCardAdded
            ? 'Add a payment method to continue without interruption.'
            : 'Explore our plans & pricing to suit your needs.'} */}
          <ManageSubscriptions
            buttonType={'link'}
            isAnchor
            anchorText={!subscriberMeta.hasCardAdded ? 'Add a payment method' : 'Explore our plans & pricing'}
            redirect_url={
              process.env.NEXT_PUBLIC_HOST_URL !== 'http://localhost:3000'
                ? `${process.env.NEXT_PUBLIC_HOST_URL}/app/content-ai-home`
                : 'http://app-predev.quattr.com/app/content-ai-home'
            }
            customer_id={showSubscriberBanner}
          />{' '}
          {!subscriberMeta.hasCardAdded ? 'to continue without interruptions.' : 'to suit your needs.'}
        </p>
        <button
          onClick={() => {
            dispatch({
              type: actionTypes?.SET_SHOW_SUBSCRIPTION_BANNER,
              showSubscriberBanner: false,
            });
          }}
          style={{
            position: 'absolute',
            right: '10px',
            top: '15px',
            transform: 'translateY(-50%)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
          }}
        >
          <b> &#10005;</b> {/* This is a Unicode character for a cross symbol */}
        </button>
      </div>
    ) : undefined;

  //for checking the magic tool icon visibility
  const isMagicIconVisible = () => {
    const currentTab = selectedUserType || getUserType();
    if (isMobile) {
      //hide for mobile screen
      return false;
    } else if (
      [SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED, SUBSCRIPTION_STATUS.SUBSCRIPTION_PAUSED].includes(
        session?.session?.user?.subscription_status
      )
    ) {
      //hide if subscription cancelled or paused
      return false;
    } else if (session?.session?.user?.userType === USER_CLASSIFICATION?.QWA_TOOLS) {
      //hide if tools user.
      return false;
    } else if (
      session?.session?.user?.userType === USER_CLASSIFICATION?.CONTENT_AI &&
      currentTab === USER_CLASSIFICATION.QWA_TOOLS &&
      [SUBSCRIPTION_STATUS.SUBSCRIPTION_CREATED, SUBSCRIPTION_STATUS.SUBSCRIPTION_TRIAL_END_REMINDER].includes(
        session?.session?.user?.subscription_status
      )
    ) {
      // hide if user is content ai trial user and view's tools tab.
      return false;
    } else {
      //visible for all other users
      return true;
    }
  };

  // handling the user type toggle tracing the locked options and redirecting to particular paths.
  const _handleUserToggle = (value) => {
    const selectedOption = options.find((option) => option.value === value);
    const isLockedValue = selectedOption.isLocked;
    setSelectedUserType(value);
    if (isLockedValue && value === USER_CLASSIFICATION.AI_SEO_SUITE) {
      router.push('/app/ai-seo-suite-home');
    } else if (isLockedValue && value === USER_CLASSIFICATION.CONTENT_AI) {
      router.push('/app/content-ai-home');
    } else {
      setSelectedUserType(value);
      if (value === USER_CLASSIFICATION.CONTENT_AI && !pathname.includes('/app/content-ai-home')) {
        router.push('/app/content-ai-home');
      } else if (
        value === USER_CLASSIFICATION.QWA_TOOLS &&
        !pathname.includes('/app/ai-tools-home', '/app/content-ai-home')
      ) {
        router.push('/app/ai-tools-home');
      } else if (
        value === USER_CLASSIFICATION.AI_SEO_SUITE &&
        !pathname.includes('/app/trends/growth-trends') &&
        !pathname.includes('/app/discoverability/')
      ) {
        router.push('/app/trends/growth-trends');
      }
    }
  };

  //set initial user type
  useEffect(() => {
    const userType = getUserType();
    if (userType === USER_CLASSIFICATION.CONTENT_AI) {
      // If this Content AI customer has been granted any AI-SEO-only nav
      // (e.g. Trends > AI Visibility) via sidebar_link_config, suppress the
      // misleading lock icon on the AI SEO Suite toggle. Routing is left
      // untouched (isLocked stays true) so switching still lands on the
      // AI SEO Suite home rather than a locked Trends page. Gated on
      // AI-SEO-exclusive keys, so other Content AI customers are unaffected.
      const customerData = customerInfo.find((o) => o.cust_code === schemaName);
      const sideBarConfig = customerData?.sidebar_link_config;
      const hasAnyAiSeoNavEnabled = aiSeoExclusiveNavKeys.some((key) => sideBarConfig?.[key]);
      setOptions([
        {
          text: 'AI SEO Suite',
          value: 'AI_SEO_SUITE',
          disabled: false,
          isLocked: true,
          hideLockIcon: hasAnyAiSeoNavEnabled,
          hidden: false,
        },
        { text: 'Content AI', value: 'CONTENT_AI', disabled: false, isLocked: false, hidden: false },
        { text: 'AI Tools', value: 'QWA_TOOLS', disabled: false, isLocked: false, hidden: false },
      ]);
    } else if (userType === USER_CLASSIFICATION.QWA_TOOLS) {
      setOptions([
        { text: 'AI SEO Suite', value: 'AI_SEO_SUITE', disabled: true, isLocked: true, hidden: false },
        { text: 'Content AI', value: 'CONTENT_AI', disabled: false, isLocked: true, hidden: false },
        { text: 'AI Tools', value: 'QWA_TOOLS', disabled: false, isLocked: false, hidden: false },
      ]);
    } else if (getUserType() == undefined) {
      setOptions([
        { text: 'AI SEO Suite', value: 'AI_SEO_SUITE', disabled: false, isLocked: false, hidden: false },
        { text: 'Content AI', value: 'CONTENT_AI', disabled: false, isLocked: false, hidden: true },
        { text: 'AI Tools', value: 'QWA_TOOLS', disabled: false, isLocked: false, hidden: false },
      ]);
    } else {
      setOptions([
        { text: 'AI SEO Suite', value: 'AI_SEO_SUITE', disabled: false, isLocked: false, hidden: false },
        { text: 'Content AI', value: 'CONTENT_AI', disabled: false, isLocked: false, hidden: false },
        { text: 'AI Tools', value: 'QWA_TOOLS', disabled: false, isLocked: false, hidden: false },
      ]);
    }
  }, [session, customerInfo]);

  //handle home screen icons clicks and path change.

  useEffect(() => {
    if (
      router.pathname.startsWith('/app/ai-tools/') ||
      ['/app/ai-tools-home', '/app/add-credits', '/app/manage-account'].includes(router.pathname)
    ) {
      setSelectedUserType(USER_CLASSIFICATION.QWA_TOOLS);
    } else if (
      ['/app/content-ai-home'].includes(router.pathname) ||
      router.pathname.startsWith('/app/content-ai-home')
    ) {
      setSelectedUserType(USER_CLASSIFICATION.CONTENT_AI);
    } else if (
      getUserType() === USER_CLASSIFICATION.CONTENT_AI &&
      (['/app/manage-lists/keywords-list', '/app/content-ai-home'].includes(router.pathname) ||
        router.pathname.startsWith('/app/content-recommendations'))
    ) {
      setSelectedUserType(USER_CLASSIFICATION.CONTENT_AI);
    } else if (router.pathname === '/app/ai-seo-suite-home') {
      setSelectedUserType(USER_CLASSIFICATION.AI_SEO_SUITE);
    }
  }, [router.pathname]);

  const handleCustomPopup = (value) => {
    dispatch({
      type: actionTypes?.SET_CUSTOM_POPUP_STATUS,
      customPopupConfig: { ...customPopupConfig, showPopup: value },
    });
  };

  const isDisplayUseDesktopAlert = useMemo(() => {
    const path1 = router?.pathname?.includes('ai-tools');
    const path2 = router?.pathname?.includes('ai-tools-home');
    return isMobile && isFreeTrailContentAIUser && !(path1 || path2);
  }, [isMobile, router?.pathname, isFreeTrailContentAIUser]);

  // Screen Size Warning for Content Recommendations routes
  const [showScreenSizeWarning, setShowScreenSizeWarning] = useState(false);
  const shouldShowScreenSizeWarning = useMemo(() => {
    // Only show on client-side when window is available
    if (typeof window === 'undefined') return false;

    const contentRecommendationRoutes = [
      '/app/content-recommendations',
      '/app/content-recommendations/details',
      '/app/content-recommendations/content-brief/details',
    ];

    return contentRecommendationRoutes.some((route) => router.pathname.includes(route));
  }, [router.pathname]);

  useEffect(() => {
    if (shouldShowScreenSizeWarning) {
      const checkScreenSize = () => {
        setTimeout(() => {
          const width = currentWindowWidth;
          if (width < EXPECTED_SCREEN_WIDTH) {
            setShowScreenSizeWarning(true);
          }
        }, 100);
      };

      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);

      return () => window.removeEventListener('resize', checkScreenSize);
    }
  }, [shouldShowScreenSizeWarning, currentWindowWidth, router?.pathname]);

  return (
    <>
      {session.status === 'loading' || loadersArray.includes('PAGE_LOADER') ? (
        <ThreeBarLoader style={{ position: 'fixed' }} />
      ) : (
        <div className="wrapper">
          <Header
            isMobile={isMobile}
            isEnterpriseUser={getUserType() == undefined}
            defaultUser={selectedUserType || getUserType()}
            options={options}
            open={open}
            toggleDrawer={toggleDrawerOnClicks}
            handleUserToggle={_handleUserToggle}
            SubscriberBanner={SubscriberBanner}
            userType={selectedUserType || getUserType()}
            getUserType={getUserType()}
          />
          <Sidebar
            isMobile={isMobile}
            open={open}
            options={options}
            userType={selectedUserType || getUserType()}
            defaultUser={getUserType()}
            selectedUserType={selectedUserType}
            isEnterpriseUser={getUserType() == undefined}
            handleUserToggle={_handleUserToggle}
            toggleDrawer={toggleDrawer}
          />
          <div
            className={`main ${open ? 'sidebar-open' : 'sidebar-close'} `}
            style={
              showSubscriberBanner && !isMobile
                ? isMobile
                  ? { height: 'calc(100vh - 120px)', marginTop: '128px' }
                  : { height: 'calc(100vh - 94px)', marginTop: '94px' }
                : { height: 'calc(100vh - 60px)', marginTop: '40px' }
            }
          >
            {/* <IconButton onClick={() => toggleDrawer(!open)} className="sidebarToggleBtn">
              {!open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton> */}
            <div
              className={`${showFullScreen ? '' : 'main-wrapper'} ${
                isDisplayUseDesktopAlert ? 'main-wrapper-desktop-alert' : ''
              }  `}
            >
              {!showFullScreen &&
                !shoudHideQheader &&
                (isSiteUsabilityPage ? (
                  <QsectionHeader
                    exportData={{ header: getHeader(), data: usabilityList, isExporting, exportProgress }}
                    onExportAll={handleExportAll}
                  />
                ) : (
                  <QsectionHeader />
                ))}
              {loadersArray.includes('SECTIONAL-LOADER') ? (
                <div id="layout__div__private_layout_3" className="position-rel h-vertical-center">
                  <ThreeBarLoader />
                </div>
              ) : (
                <div className="ui-body-container">
                  <div>{props.children}</div>
                </div>
              )}
              {snackConfig.open ? (
                <Qalert
                  type={snackConfig.type}
                  open={snackConfig.open}
                  set={(flag) => toogleSnackBar({ open: flag })}
                  text={snackConfig.text}
                  time={snackConfig?.time || 5000}
                />
              ) : null}
              {customPopupConfig?.showPopup ? (
                <CustomAlert
                  showPopup={customPopupConfig?.showPopup}
                  style={{ fontSize: '12px' }}
                  setAlertControl={handleCustomPopup}
                  text={customPopupConfig?.text}
                  anchorText={customPopupConfig?.anchorText}
                  anchorLink={customPopupConfig?.anchorLink}
                />
              ) : null}
              {isDisplayUseDesktopAlert ? <DesktopAlert /> : null}
            </div>
          </div>
          {isMagicIconVisible() ? <AutoWriteEditor /> : null}
        </div>
      )}
      {/* Screen Size Warning for Content Recommendations routes */}
      {shouldShowScreenSizeWarning && currentWindowWidth < EXPECTED_SCREEN_WIDTH && !isDisplayUseDesktopAlert && (
        <ScreenSizeWarning
          isOpen={showScreenSizeWarning}
          onClose={() => setShowScreenSizeWarning(false)}
          requiedMinWidth={EXPECTED_SCREEN_WIDTH}
          currentWidth={currentWindowWidth}
          showOnlyOnce={true}
        />
      )}

      {/* Session Warning Modal */}
      <SessionWarningModal
        open={showWarning}
        secondsRemaining={secondsRemaining}
        isAbsoluteExpiry={isAbsoluteExpiry}
        onStayLoggedIn={extendSession}
        onLogout={sessionLogout}
        onReLogin={reLogin}
        onCancel={cancelWarning}
      />
    </>
  );
};
export default PrivateLayout;
